/**
 * @jsxRuntime classic
 * @jsx jsx
 */

import React, { memo, useCallback, useRef, useState } from 'react';

import { css, jsx, styled } from '@compiled/react';
// eslint-disable-next-line @atlaskit/platform/prefer-crypto-random-uuid -- Use crypto.randomUUID instead
import { v4 as uuid } from 'uuid';

import { token } from '@atlaskit/tokens';

import { useAnalytics } from '../../../../common/controller/analytics/use-analytics';
import { aiDefinitionsLoadUFOExperience } from '../../../common/ufo/experiences';
import { useTextHighlighterClickNative } from '../../../text-highlighter/useTextHighlighterClick';
import { onClickAutoDefinitionHighlights } from '../../analytics';
import type { KeyPhraseCategory } from '../../api/useAutohighlightSupplier';
import { DefinitionPopup } from '../popup/definition-popup';
import type { DefinitionConfigType, DefinitionSourceConfigType } from '../types';

type DefinitionTextHighlighterProps = {
	keyPhraseCategory: KeyPhraseCategory;
	definitionConfig: DefinitionConfigType;
	sourceConfig: DefinitionSourceConfigType;
};

type TriggerPosition = {
	top: number;
	left: number;
	width: number;
	height: number;
};

const definitionWrapperStyles = css({
	position: 'relative',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled, @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
const DefinitionHighlighter = styled.span<{ showHighlight: boolean }>(({ showHighlight }) => ({
	borderImage: `linear-gradient(to right, #0065FF, #BF63F3, #F5E6A8) 0 0 100% 0`,
	borderWidth: token('border.width.selected'),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	borderBottomStyle: showHighlight ? 'solid' : 'none',
	'&:hover': {
		background: `linear-gradient(to right, rgba(0, 101, 255, 0.1), rgba(191, 99, 243, 0.1))`,
		cursor: 'pointer',
	},
}));

export const DefinitionTextHighlighter: React.MemoExoticComponent<({ children, definitionConfig, keyPhraseCategory, sourceConfig, }: React.PropsWithChildren<DefinitionTextHighlighterProps>) => JSX.Element> = memo(
	({
		children,
		definitionConfig,
		keyPhraseCategory,
		sourceConfig,
	}: React.PropsWithChildren<DefinitionTextHighlighterProps>) => {
		// eslint-disable-next-line @atlaskit/platform/prefer-crypto-random-uuid -- Use crypto.randomUUID instead
		const definitionSessionId = useRef(uuid()).current;
		// controls whether to show underline highlight
		const [showHighlight, setShowHighlight] = useState(true);
		const [definitionQuery, setDefinitionQuery] = useState('');
		const [triggerPosition, setTriggerPosition] = useState<TriggerPosition | undefined>(undefined);
		// const [additionalContext, setAdditionalContext] = useState('');

		const [fireAnalyticsEvent] = useAnalytics();

		const onDefinitionSingleClick = useCallback(
			async (e: React.MouseEvent | MouseEvent) => {
				e.preventDefault();
				e.stopPropagation();
				const eventTarget = e.target;
				if (!eventTarget || !(eventTarget instanceof Element)) {
					return;
				}
				await aiDefinitionsLoadUFOExperience.start();

				setShowHighlight(false);

				fireAnalyticsEvent(
					onClickAutoDefinitionHighlights({
						contentId: sourceConfig.contentId,
						sessionId: definitionSessionId,
						keyPhraseCategory,
					}),
				);

				// const selectedContext = eventTarget.parentElement;
				// const additionalContext = selectedContext
				// 	? selectedContext.textContent || selectedContext.innerText
				// 	: '';
				const rect = eventTarget.getBoundingClientRect();
				// we only need to pass in height to popup; others are needed for manual define
				const acronymRect = {
					top: 0,
					left: 0,
					width: rect.width,
					height: rect.height,
				};
				setDefinitionQuery(eventTarget.textContent || '');
				setTriggerPosition(acronymRect);
				// setAdditionalContext(additionalContext);
			},
			[definitionSessionId, fireAnalyticsEvent, keyPhraseCategory, sourceConfig.contentId],
		);

		const onDefinitionClick = useTextHighlighterClickNative({
			singleClickCallback: onDefinitionSingleClick,
			doubleClickCallback: () => {}, // do nothing, actions menu takes over
		});

		return (
			// needed to absolute position DefinitionPopup
			<span css={definitionWrapperStyles}>
				<DefinitionHighlighter
					showHighlight={showHighlight}
					onClick={onDefinitionClick}
					data-testid="definition-highlighter"
				>
					{children}
				</DefinitionHighlighter>
				{triggerPosition ? (
					<DefinitionPopup
						onClose={() => {
							setTriggerPosition(undefined);
							setShowHighlight(true);
						}}
						triggerPosition={triggerPosition}
						queryConfig={{
							query: definitionQuery,
							additionalQueryContext: '',
						}}
						sessionConfig={{
							sessionId: definitionSessionId,
						}}
						sourceConfig={sourceConfig}
						definitionConfig={definitionConfig}
						definitionLoadingConfig={{
							width: triggerPosition.width,
						}}
						onLoad={() => setShowHighlight(true)}
					/>
				) : null}
			</span>
		);
	},
);
